/*
 * Контекст приложения — глобальное состояние (корзина, избранное, пользователь).
 * Корзина и избранное хранятся на сервере.
 */
import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import client from '../api/client';
import { getPlatform, getInitData, getUser } from '../utils/platform';
import { toast } from '../components/Toast/toast';

/** Профиль из API → состояние приложения. Единая форма для всех входов:
 *  initData-авторизация, восстановление сессии, вход по коду. */
function toAppUser(u = {}, fallback = null) {
  return {
    id: u.id || u.telegram_id || u.max_id || fallback?.id,
    first_name: u.first_name || fallback?.first_name,
    username: fallback?.username || u.username,
    is_owner: u.is_owner || false,
    is_developer: u.is_developer || false,
    is_admin: u.is_admin || false,
    role: u.role || 'user',
  };
}

const AppContext = createContext();

export function AppProvider({ children }) {
  // Корзина (загружается с сервера)
  const [cart, setCart] = useState([]);

  // Избранное (загружается с сервера)
  const [favorites, setFavorites] = useState([]);

  // Уведомления о поступлении (загружаются с сервера)
  const [notifications, setNotifications] = useState([]);

  // Пользователь
  const [user, setUser] = useState(null);

  // Проверка входа завершена (на web). Нужно, чтобы при 401/сетевой ошибке
  // показывать форму входа, а не вечный спиннер (user === null).
  const [authReady, setAuthReady] = useState(false);

  // Состояние истечения подписки
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);

  // Очистка старых localStorage ключей при старте
  useEffect(() => {
    localStorage.removeItem('sova-cart');
    localStorage.removeItem('sova-favorites');
  }, []);

  // Загрузка корзины и избранного с сервера
  const loadUserData = useCallback(async () => {
    try {
      const [cartRes, favRes] = await Promise.all([
        client.get('/cart'),
        client.get('/favorites'),
      ]);
      setCart(cartRes.data || []);
      setFavorites(favRes.data || []);
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
    }
  }, []);

  // Загрузка пользователя из Telegram/MAX WebApp
  const authCompleted = useRef(false);

  const doAuth = useCallback(async () => {
    const platform = getPlatform();

    if (platform === 'web') {
      return false;
    }

    const user = getUser();
    const initData = getInitData();
    if (!user) return false;

    const payload = initData
      ? { init_data: initData, platform }
      : { telegram_id: user.id, username: user.username, first_name: user.first_name, platform };

    const delays = [1000, 3000, 9000];
    for (let i = 0; i < delays.length; i++) {
      try {
        const res = await client.post('/users/auth', payload);
        const u = res.data.user || res.data;
        if (res.data.token) localStorage.setItem('sova-token', res.data.token);
        authCompleted.current = true;
        setUser(toAppUser(u, user));
        // Загружаем корзину и избранное после авторизации
        await loadUserData();
        return true;
      } catch (err) {
        if (i < delays.length - 1) await new Promise(r => setTimeout(r, delays[i]));
      }
    }

    // Все попытки провалились — сообщаем об ошибке, а не молчим (админ иначе
    // получает беззвучный редирект без объяснения причин)
    toast.error('Не удалось войти: нет связи с сервером. Проверьте интернет и откройте магазин заново.');
    authCompleted.current = true;
    setUser(toAppUser({}, user));
    return true;
  }, [loadUserData]);

  // Переавторизация при возврате в приложение
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && authCompleted.current) {
        doAuth();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [doAuth]);

  useEffect(() => {
    const platform = getPlatform();
    if (platform === 'web') {
      // Сессия с компьютера: восстанавливаем профиль по сохранённому токену
      // (вход по коду из бота), иначе после перезагрузки пользователь — null.
      const restore = async () => {
        if (!localStorage.getItem('sova-token')) {
          authCompleted.current = true;
          setAuthReady(true);
          return;
        }
        try {
          // timeout обязателен: без него зависший запрос держал бы спиннер
          // вечно (у axios нет таймаута по умолчанию)
          const res = await client.get('/users/me', { timeout: 15000 });
          setUser(toAppUser(res.data || {}));
          await loadUserData();
        } catch (_) {
          // Протухший токен снят интерцептором 401 или нет связи —
          // остаёмся на форме входа (authReady см. finally)
        } finally {
          authCompleted.current = true;
          setAuthReady(true);
        }
      };
      restore();
      return;
    }

    const tryAuth = () => {
      const user = getUser();
      if (!user) return false;
      doAuth();
      return true;
    };

    if (!tryAuth()) {
      const interval = setInterval(() => {
        if (tryAuth()) clearInterval(interval);
      }, 200);
      const timeout = setTimeout(() => {
        clearInterval(interval);
        if (authCompleted.current) return;
        authCompleted.current = true;
      }, 5000);
      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [doAuth, loadUserData]);

  // === Вход по коду из бота (сайт и админка с компьютера) ===

  const requestLoginCode = useCallback(async (phone, platform) => {
    const res = await client.post('/users/auth-code/request', { phone, platform });
    return res.data;
  }, []);

  const loginWithCode = useCallback(async (phone, code) => {
    const res = await client.post('/users/auth-code/verify', { phone, code });
    const { token, user: u } = res.data || {};
    if (!token) throw new Error('Сервер не вернул токен');
    localStorage.setItem('sova-token', token);
    const mapped = toAppUser(u || {});
    authCompleted.current = true;
    setUser(mapped);
    await loadUserData();
    return mapped;
  }, [loadUserData]);

  // === Корзина (серверная) ===

  const addToCart = useCallback(async (product, quantity = 1, variant = null) => {
    // Кламп к остатку ВЫБРАННОГО варианта, без варианта — к общему на товаре
    const stock = variant
      ? (typeof variant.quantity === 'number' ? variant.quantity : 999)
      : (typeof product.quantity === 'number' ? product.quantity : 999);
    const qty = Math.min(quantity, stock);
    if (qty <= 0) return;
    try {
      await client.post(`/cart/${product.id}`, {
        quantity: qty,
        variant_id: variant ? variant.id : undefined,
      });
      setCart(prev => {
        const vId = variant ? variant.id : null;
        const existing = prev.find(item => item.id === product.id && (item.variant_id || null) === vId);
        if (existing) {
          return prev.map(item =>
            item.id === product.id && (item.variant_id || null) === vId
              ? { ...item, quantity: Math.min(item.quantity + qty, stock) }
              : item
          );
        }
        return [...prev, {
          id: product.id,
          name: product.name,
          price: Number(variant ? variant.price : product.price),
          image_url: product.image_url,
          in_stock: product.in_stock,
          quantity_on_hand: stock,
          quantity: qty,
          variant_id: vId,
          variant_label: variant ? variant.label : null,
        }];
      });
    } catch (err) {
      console.error('Ошибка добавления в корзину:', err);
      toast.error(err.response?.data?.detail || 'Не удалось добавить в корзину');
    }
  }, []);

  const removeFromCart = useCallback(async (productId, variantId = null) => {
    try {
      await client.delete(`/cart/${productId}`, { params: variantId ? { variant_id: variantId } : {} });
      setCart(prev => prev.filter(item =>
        !(item.id === productId && (item.variant_id || null) === (variantId || null))
      ));
    } catch (err) {
      console.error('Ошибка удаления из корзины:', err);
    }
  }, []);

  const updateCartQuantity = useCallback(async (productId, quantity, variantId = null) => {
    const item = cart.find(i => i.id === productId && (i.variant_id || null) === (variantId || null));
    const maxQty = typeof item?.quantity_on_hand === 'number' ? item.quantity_on_hand : 999;
    const q = Math.min(quantity, maxQty);
    if (q <= 0) {
      removeFromCart(productId, variantId);
      return;
    }
    try {
      await client.put(`/cart/${productId}`, { quantity: q, variant_id: variantId || undefined });
      setCart(prev =>
        prev.map(i =>
          i.id === productId && (i.variant_id || null) === (variantId || null)
            ? { ...i, quantity: q }
            : i
        )
      );
    } catch (err) {
      console.error('Ошибка обновления количества:', err);
    }
  }, [removeFromCart, cart]);

  const clearCart = useCallback(async () => {
    try {
      await client.delete('/cart');
      setCart([]);
    } catch (err) {
      console.error('Ошибка очистки корзины:', err);
    }
  }, []);

  const refreshCart = useCallback(async () => {
    try {
      const res = await client.get('/cart');
      setCart(res.data || []);
    } catch (err) {
      console.error('Ошибка обновления корзины:', err);
    }
  }, []);

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [cart]);

  const cartCount = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  // === Избранное (серверное) ===

  const toggleFavorite = useCallback(async (product) => {
    const exists = favorites.some(f => f.id === product.id);
    try {
      if (exists) {
        await client.delete(`/favorites/${product.id}`);
        setFavorites(prev => prev.filter(f => f.id !== product.id));
      } else {
        await client.post(`/favorites/${product.id}`);
        setFavorites(prev => [...prev, product]);
      }
    } catch (err) {
      console.error('Ошибка избранного:', err);
    }
  }, [favorites]);

  const favoritesSet = useMemo(() => new Set(favorites.map(f => f.id)), [favorites]);

  const isFavorite = useCallback((productId) => {
    return favoritesSet.has(productId);
  }, [favoritesSet]);

  const refreshFavorites = useCallback(async () => {
    try {
      const res = await client.get('/favorites');
      setFavorites(res.data || []);
    } catch (err) {
      console.error('Ошибка обновления избранного:', err);
    }
  }, []);

  const clearFavorites = useCallback(async () => {
    try {
      await client.delete('/favorites');
      setFavorites([]);
    } catch (err) {
      console.error('Ошибка очистки избранного:', err);
    }
  }, []);

  // === Уведомления о поступлении ===

  useEffect(() => {
    if (!user) return;
    const loadNotifications = async () => {
      try {
        const res = await client.get('/notifications/');
        setNotifications(res.data.map(item => item.product));
      } catch (err) {
        console.error('Ошибка загрузки подписок:', err);
      }
    };
    loadNotifications();
  }, [user]);

  const notificationsSet = useMemo(() => new Set(notifications.map(n => n.id)), [notifications]);

  const toggleNotification = useCallback(async (product) => {
    const exists = notificationsSet.has(product.id);
    try {
      if (exists) {
        await client.delete(`/notifications/subscribe/${product.id}`);
        setNotifications(prev => prev.filter(item => item.id !== product.id));
      } else {
        await client.post(`/notifications/subscribe/${product.id}`);
        setNotifications(prev => [...prev, product]);
      }
      return { success: true };
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Ошибка подписки';
      return { success: false, error: msg };
    }
  }, [notificationsSet]);

  const isNotified = useCallback((productId) => {
    return notificationsSet.has(productId);
  }, [notificationsSet]);

  const value = useMemo(() => ({
    cart,
    favorites,
    user,
    setUser,
    authReady,
    subscriptionExpired,
    setSubscriptionExpired,
    addToCart,
    updateCartQuantity,
    removeFromCart,
    clearCart,
    refreshCart,
    cartTotal,
    cartCount,
    toggleFavorite,
    isFavorite,
    refreshFavorites,
    clearFavorites,
    notifications,
    toggleNotification,
    isNotified,
    requestLoginCode,
    loginWithCode,
  }), [cart, favorites, user, authReady, subscriptionExpired, cartTotal, cartCount, notifications, addToCart, updateCartQuantity, removeFromCart, clearCart, refreshCart, toggleFavorite, isFavorite, refreshFavorites, clearFavorites, toggleNotification, isNotified, requestLoginCode, loginWithCode]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
